import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/use-auth';
import { useToast } from '@/hooks/use-toast';

export interface UserProfile {
  id: string;
  full_name: string | null;
  email: string | null;
  role: string;
  company_id: string | null;
  location_id: string | null;
  created_at: string;
  // Extended properties for UI
  status: 'active' | 'inactive' | 'pending';
  deviceTags: string[];
  lastLogin?: Date;
}

export interface UserRole {
  value: string;
  label: string;
}

export const USER_ROLES: UserRole[] = [
  { value: 'admin', label: 'Yönetici' },
  { value: 'manager', label: 'Müdür' },
  { value: 'sales', label: 'Satış' },
  { value: 'cashier', label: 'Kasiyer' }
];

export const useUsers = () => {
  const { companyId } = useAuth();
  const { toast } = useToast();
  const [users, setUsers] = useState<UserProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchUsers = async () => {
    if (!companyId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const { data: usersData, error: usersError } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('company_id', companyId)
        .order('created_at', { ascending: false });

      if (usersError) {
        throw usersError;
      }

      // Transform database data to match UI expectations
      const transformedUsers: UserProfile[] = (usersData || []).map(user => ({
        id: user.id,
        full_name: user.full_name,
        email: user.email,
        role: user.role,
        company_id: user.company_id,
        location_id: user.location_id ?? null,
        created_at: user.created_at,
        // These would ideally come from additional tables or fields
        status: 'active', // This could be determined from last_login or an active field
        deviceTags: [], // This could come from a user_device_tags table
        lastLogin: new Date(user.created_at) // This could come from an auth_logs table
      }));

      setUsers(transformedUsers);

    } catch (err: any) {
      const errorMessage = err.message || 'Kullanıcılar yüklenirken hata oluştu';
      setError(errorMessage);
      console.error('Error fetching users:', err);
    } finally {
      setLoading(false);
    }
  };

  const updateUserRole = async (userId: string, newRole: string) => {
    try {
      const { error } = await supabase
        .from('user_profiles')
        .update({ role: newRole })
        .eq('id', userId);

      if (error) {
        throw error;
      }

      // Update local state
      setUsers(prevUsers => 
        prevUsers.map(user => 
          user.id === userId ? { ...user, role: newRole } : user
        )
      );

      toast({
        title: "Başarılı",
        description: "Kullanıcı rolü güncellendi",
      });

      return true;
    } catch (err: any) {
      const errorMessage = err.message || 'Kullanıcı rolü güncellenirken hata oluştu';
      toast({
        title: "Hata",
        description: errorMessage,
        variant: "destructive",
      });
      console.error('Error updating user role:', err);
      return false;
    }
  };

  const createUser = async (userData: {
    email: string;
    full_name: string;
    role: string;
    password: string;
  }) => {
    if (!companyId) {
      toast({
        title: "Hata",
        description: "Şirket ID bulunamadı",
        variant: "destructive",
      });
      return false;
    }

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('Oturum bulunamadı');
      }

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL || 'https://yckhhzgqlfgwcwfmugow.supabase.co'}/functions/v1/manage-users?action=create-user`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`,
            'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY || 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Inlja2hoemdxbGZnd2N3Zm11Z293Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3NTY0MTQwMzgsImV4cCI6MjA3MTk5MDAzOH0.kAcPWINVmWEB8uIZL3qp8fGBXoiIrVrEBPj9LRfpUJA',
          },
          body: JSON.stringify(userData),
        }
      );

      const result = await response.json();

      if (!response.ok) {
        if (result.error === 'subscription_required') {
          throw new Error('subscription_required');
        }
        throw new Error(result.error || 'Kullanıcı oluşturulamadı');
      }

      // Refetch users to get the complete profile
      await fetchUsers();

      toast({
        title: "Başarılı",
        description: "Kullanıcı oluşturuldu",
      });

      return true;
    } catch (err: any) {
      const errorMessage = err.message || 'Kullanıcı oluşturulurken hata oluştu';
      if (errorMessage !== 'subscription_required') {
        toast({
          title: "Hata",
          description: errorMessage,
          variant: "destructive",
        });
      }
      console.error('Error creating user:', err);
      return false;
    }
  };

  const updateUserLocation = async (userId: string, locationId: string | null) => {
    try {
      const { error } = await supabase
        .from('user_profiles')
        .update({ location_id: locationId })
        .eq('id', userId);

      if (error) {
        throw error;
      }

      setUsers(prevUsers =>
        prevUsers.map(user =>
          user.id === userId ? { ...user, location_id: locationId } : user
        )
      );

      toast({
        title: "Başarılı",
        description: locationId ? "Kullanıcı şubeye atandı" : "Kullanıcı şubeden çıkarıldı",
      });

      return true;
    } catch (err: any) {
      const errorMessage = err.message || 'Kullanıcı şubesi güncellenirken hata oluştu';
      toast({
        title: "Hata",
        description: errorMessage,
        variant: "destructive",
      });
      console.error('Error updating user location:', err);
      return false;
    }
  };

  const deleteUser = async (userId: string) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('Oturum bulunamadı');
      }

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL || 'https://yckhhzgqlfgwcwfmugow.supabase.co'}/functions/v1/manage-users?action=delete-user`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`,
            'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY || 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Inlja2hoemdxbGZnd2N3Zm11Z293Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3NTY0MTQwMzgsImV4cCI6MjA3MTk5MDAzOH0.kAcPWINVmWEB8uIZL3qp8fGBXoiIrVrEBPj9LRfpUJA',
          },
          body: JSON.stringify({ userId }),
        }
      );

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Kullanıcı silinemedi');
      }

      // Remove from local state
      setUsers(prevUsers => prevUsers.filter(user => user.id !== userId));

      toast({
        title: "Başarılı",
        description: "Kullanıcı silindi",
      });

      return true;
    } catch (err: any) {
      const errorMessage = err.message || 'Kullanıcı silinirken hata oluştu';
      toast({
        title: "Hata",
        description: errorMessage,
        variant: "destructive",
      });
      console.error('Error deleting user:', err);
      return false;
    }
  };

  const deleteAccount = async (confirmEmail: string) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('Oturum bulunamadı');
      }

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL || 'https://yckhhzgqlfgwcwfmugow.supabase.co'}/functions/v1/manage-users?action=delete-account`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`,
            'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY || 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Inlja2hoemdxbGZnd2N3Zm11Z293Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3NTY0MTQwMzgsImV4cCI6MjA3MTk5MDAzOH0.kAcPWINVmWEB8uIZL3qp8fGBXoiIrVrEBPj9LRfpUJA',
          },
          body: JSON.stringify({ confirmEmail }),
        }
      );

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Hesap silinemedi');
      }

      toast({
        title: "Başarılı",
        description: "Hesabınız silindi",
      });

      return true;
    } catch (err: any) {
      const errorMessage = err.message || 'Hesap silinirken hata oluştu';
      toast({
        title: "Hata",
        description: errorMessage,
        variant: "destructive",
      });
      console.error('Error deleting account:', err);
      return false;
    }
  };

  useEffect(() => {
    void fetchUsers();
  }, [companyId]);

  return {
    users,
    loading,
    error,
    updateUserRole,
    updateUserLocation,
    createUser,
    deleteUser,
    deleteAccount,
    refetch: fetchUsers
  };
};
